import React, { useEffect, useRef, useState } from 'react'
import { Helmet } from 'react-helmet-async'
import ImageCarousel from '../components/ImageCaroussel'
import { useAnnouncement } from '../context/AnnouncementContext'
import '../views/HomeSection.css'
import '../views/FriterieSection.css'

const menuImages = [
  {
    src: 'https://images.pexels.com/photos/5041473/pexels-photo-5041473.jpeg?auto=compress&cs=tinysrgb&w=900',
    alt: 'Frites dorées',
  },
  {
    src: 'https://images.pexels.com/photos/17321493/pexels-photo-17321493.jpeg?auto=compress&cs=tinysrgb&w=900',
    alt: 'Croquettes belges',
  },
  {
    src: 'https://images.pexels.com/photos/31701975/pexels-photo-31701975.jpeg?auto=compress&cs=tinysrgb&w=900',
    alt: 'Frites spéciales',
  },
  {
    src: 'https://images.pexels.com/photos/14686443/pexels-photo-14686443.jpeg?auto=compress&cs=tinysrgb&w=900',
    alt: 'Menu complet',
  },
]

const patrimoineImages = [
  {
    src: '/img/Chateau Ecaussinnes1.jpeg',
    alt: "Château d'Écaussinnes-Lalaing",
  },
  {
    src: '/img/Folie2.jpeg',
    alt: 'Château de la Follie et son parc',
  },
  {
    src: '/img/Houssiere1.jpg',
    alt: 'Sentier dans le Bois de la Houssière',
  },
  {
    src: '/img/Marlière1.jpg',
    alt: 'Ferme Marlière à Écaussinnes',
  },
  {
    src: '/img/Ronquières1.webp',
    alt: 'Vue du canal près de Ronquières',
  },
  {
    src: '/img/Canal1.jpg',
    alt: "Canal proche d'Écaussinnes",
  },
]

const MAP_LOAD_TIMEOUT_MS = 8000

const Home = () => {
  const mapsUrl = `https://www.google.com/maps/place/Friterie+de+Lalaing/@50.5694754,4.1772614,19z/data=!4m6!3m5!1s0x47c2357a38ceaa6d:0x83fba363582b0fec!8m2!3d50.5694839!4d4.1777147!16s%2Fg%2F1tm11rz0?entry=ttu&g_ep=EgoyMDI2MDEwNi4wIKXMDSoASAFQAw%3D%3D`

  const { announcement, announcementActive } = useAnnouncement()

  const [mapError, setMapError] = useState(false)
  const mapTimeoutRef = useRef(null)
  const fadeRefs = useRef([])

  useEffect(() => {
    mapTimeoutRef.current = setTimeout(() => setMapError(true), MAP_LOAD_TIMEOUT_MS)
    return () => clearTimeout(mapTimeoutRef.current)
  }, [])

  const handleMapLoad = () => {
    clearTimeout(mapTimeoutRef.current)
  }

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible')
            observer.unobserve(entry.target)
          }
        })
      },
      { threshold: 0.12 }
    )
    fadeRefs.current.forEach((el) => { if (el) observer.observe(el) })
    return () => observer.disconnect()
  }, [])

  return (
    <div className="home-container1">
      <Helmet>
        <title>Friterie de Lalaing</title>
        <meta property="og:title" content="Friterie de Lalaing" />
        <link rel="canonical" href="https://friteriedelalaing.be" />
      </Helmet>

      {/* HERO */}
      <section className="friterie-hero">
        <div className="hero">
          <img src="/img/logo_Lalaing.png" className="hero-logo" alt="Logo" />
        </div>
        <div className="friterie-hero-content">
          <p className="friterie-hero-subtitle">
            Frites maison et spécialités belges au cœur d'Écaussinnes-Lalaing
          </p>
        </div>
        {announcementActive && announcement?.heroClosedBadgeActive && (
          <div className="hero-closed-badge" aria-label="Magasin fermé temporairement">
            FERMÉE
          </div>
        )}
        <div className="scroll-indicator" aria-hidden="true">
          <p className="friterie-hero-subtitle" id="Bienvenue">
            Bienvenue
          </p><span className="scroll-indicator-chevron"></span></div>
      </section>

      {/* ANNONCE PONCTUELLE – visible sur l'accueil quand active */}
      {announcementActive && (
        <div className="menu-announcement-banner" role="status" aria-live="polite">
          <span className="menu-announcement-icon">📢</span>
          <div className="menu-announcement-text">
            <strong>Annonce</strong>
            <span>{announcement.message}</span>
          </div>
        </div>
      )}


      {/* SECTION MENU : carte home-menu, image gauche / texte droite */}
      <section
        className="home-menu-section fade-up"
        id="menu"
        ref={(el) => { fadeRefs.current[0] = el }}
      >
        <div className="home-menu-card">
          <div className="home-menu-media">
            <ImageCarousel
              images={menuImages}
              interval={3500}
              height="250px"
              showControls={false}
              showIndicators={true}
            />
          </div>

          <div className="home-menu-text">
            <h2 className="section-title">Nos frites &amp; spécialités</h2>
            <p className="section-content">
              Frites maison croustillantes, croquettes, fricadelles et une sélection de sauces
              généreuses pour tous les goûts.
            </p>
            <a href="/menu" className="btn btn-accent">
              Voir le menu complet
            </a>
          </div>
        </div>
      </section>

      {/* SECTION PATRIMOINE : carte home-about, texte gauche / image droite */}
      <section
        className="home-about-section fade-up"
        id="histoire"
        ref={(el) => { fadeRefs.current[1] = el }}
      >
        <div className="home-about-card">
          <div className="home-about-text">
            <h2 className="section-title">Découvrir Écaussinnes-Lalaing</h2>
            <p className="section-content">
              Nichée dans la Rue de la Grande Ronce, la friterie s&apos;inscrit dans un village chargé
              d&apos;histoire&nbsp;: château, ponts, rues anciennes… Un arrêt gourmand au cœur du
              patrimoine local.
            </p>
            <a href="/patrimoine" className="btn btn-secondary">
              Explorer le patrimoine
            </a>
          </div>

          <div className="home-about-media">
            <ImageCarousel
              images={patrimoineImages}
              interval={2000}
              height="250px"
              showControls={false}
              showIndicators={true}
            />
            <div className="home-about-caption">
              Le patrimoine historique d&apos;Écaussinnes
            </div>
          </div>
        </div>
      </section>

      {/* SECTION CARTE + PARKINGS */}
      <section
        className="hero-map-section fade-up"
        aria-label="Localisation et parkings"
        ref={(el) => { fadeRefs.current[2] = el }}
      >
        <div className="hero-map-card">
          <div className="hero-map-embed">
            {mapError ? (
              <div className="hero-map-fallback">
                <p>La carte interactive n&apos;a pas pu être chargée.</p>
                <a href={mapsUrl} target="_blank" rel="noreferrer" className="btn btn-accent">
                  Ouvrir dans Google Maps
                </a>
              </div>
            ) : (
              <iframe
                src="https://www.google.com/maps/d/u/0/embed?mid=1t_QtekQGeJaOoksLVTrM7cdNtXOsEwE&ehbc=2E312F&noprof=1"
                width="640"
                height="480"
                title="Carte Friterie de Lalaing"
                referrerPolicy="no-referrer-when-downgrade"
                onLoad={handleMapLoad}
              ></iframe>
            )}
          </div>

          <div className="hero-map-legend">
            <h2 className="hero-map-title">Où nous trouver&nbsp;?</h2>
            <p className="hero-map-address">
              Rue de la Grande Ronce 7<br />
              7191 Écaussinnes-Lalaing
            </p>

            <ul className="hero-map-list">
              <li>
                <span className="hero-map-dot hero-map-dot--friterie" />
                <span>Friterie de Lalaing (A)</span>
              </li>
              <li>
                <span className="hero-map-dot hero-map-dot--parking" />
                <span>Parking Place des Comtes (B)</span>
              </li>
              <li>
                <span className="hero-map-dot hero-map-dot--parking" />
                <span>Parking de la Grand&apos;Place (C)</span>
              </li>
            </ul>

            <div className="hero-map-actions">
              <a href={mapsUrl} target="_blank" rel="noreferrer" className="btn btn-accent">
                Ouvrir dans Google Maps
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}

export default Home
